import { SCORE_LEGEND, targetSummary } from '../shared/domain';
import { aggregateLabel, aggregateScore } from '../shared/score';
import { reportStatus, type ReportStatus } from '../shared/status';
import { LANES, laneTitle, type SavedCriterion } from '../shared/lanes';
import {
  ApiError,
  deleteLane,
  downloadReportPdf,
  fetchFeed,
  fetchLane,
  fetchLaneCriteria,
  fetchPeople,
  isOffline,
  reviewReport,
  saveLane,
  uploadPhoto,
  type AuthUser,
  type Lane,
  type Person,
  type Spot,
} from './api';
import {
  clearWorkingDraft,
  dropQueue,
  enqueue,
  loadWorkingDraft,
  saveWorkingDraft,
  type PendingPhoto,
} from './offline';
import { referenceChips } from './library';
import { buildTargets, peopleField } from './people';
import { photoField } from './photos';
import { photoGallery } from './photos';
import { emptyPlace, locationField } from './place';
import {
  bylines,
  el,
  fmtTime,
  formatPlace,
  fromLocalInput,
  go,
  statusPill,
  textInput,
  textarea,
  toast,
  toLocalInput,
} from './ui';

interface LaneState {
  serverId: string;
  clientId: string;
  step: 1 | 2 | 3;
  scoreIndex: number;
  laneId: string;
  observedAt: string;
  elements: string[];
  tokens: string[];
  lat: string;
  lng: string;
  accuracy: string;
  description: string;
  context: string;
  linkedSpotIds: string[];
  scores: Record<string, number | 'na'>;
  comments: Record<string, string>;
  criteria: SavedCriterion[];
  strengths: string;
  deficiencies: string;
  remediation: string;
  narrative: string;
  excon: string;
  evidenceKeys: string[];
  references: string[];
  status: ReportStatus;
}

function blankState(): LaneState {
  return {
    serverId: '',
    clientId: crypto.randomUUID(),
    step: 1,
    scoreIndex: 0,
    laneId: '',
    observedAt: toLocalInput(new Date()),
    elements: [],
    tokens: [],
    lat: '',
    lng: '',
    accuracy: '',
    description: '',
    context: '',
    linkedSpotIds: [],
    scores: {},
    comments: {},
    criteria: [],
    strengths: '',
    deficiencies: '',
    remediation: '',
    narrative: '',
    excon: '',
    evidenceKeys: [],
    references: [],
    status: 'draft',
  };
}

function fromServer(lane: Lane): LaneState {
  return {
    serverId: lane.id,
    clientId: lane.client_id || crypto.randomUUID(),
    step: 1,
    scoreIndex: 0,
    laneId: lane.lane_id,
    observedAt: toLocalInput(new Date(lane.observed_at)),
    elements: lane.elements?.length ? lane.elements : lane.element ? [lane.element] : [],
    tokens: lane.targets?.tokens || [],
    lat: lane.location_lat == null ? '' : String(lane.location_lat),
    lng: lane.location_lng == null ? '' : String(lane.location_lng),
    accuracy: lane.location_accuracy_m == null ? '' : String(lane.location_accuracy_m),
    description: lane.location_description || '',
    context: lane.context || '',
    linkedSpotIds: lane.linked_spot_ids || [],
    scores: { ...(lane.scores || {}) },
    comments: { ...(lane.comments || {}) },
    criteria: lane.criteria?.length
      ? lane.criteria.map((item) => ({ ...item, lookFor: [...(item.lookFor || [])] }))
      : [],
    strengths: lane.strengths || '',
    deficiencies: lane.deficiencies || '',
    remediation: lane.remediation || '',
    narrative: lane.narrative || '',
    excon: lane.excon_comments || '',
    evidenceKeys: lane.evidence_keys || [],
    references: lane.references || [],
    status: reportStatus(lane.status),
  };
}

export function renderLane(
  root: HTMLElement,
  opts: { user: AuthUser; laneId?: string; fresh?: boolean }
): () => void {
  let state = blankState();
  let people: Person[] = [];
  let spots: Spot[] = [];
  let catalog: Record<string, SavedCriterion[]> = {};

  function publishedCriteria(laneId: string): SavedCriterion[] {
    return (catalog[laneId] || []).map((item) => ({
      id: item.id,
      title: item.title,
      prompt: item.prompt || '',
      lookFor: [...(item.lookFor || [])],
    }));
  }
  let alive = true;
  let persist = false;
  const photos = photoField();

  const who = peopleField([]);
  const place = locationField();

  function categories(): SavedCriterion[] {
    return state.criteria;
  }

  function readCriteria(): void {
    const cat = state.criteria[state.scoreIndex];
    if (!cat || !root.querySelector('[data-crit-title]')) return;
    cat.title = readBox('[data-crit-title]') ?? cat.title;
    cat.prompt = readBox('[data-crit-prompt]') ?? cat.prompt;
    cat.lookFor = [...root.querySelectorAll<HTMLTextAreaElement>('[data-crit-line]')].map(
      (node) => node.value
    );
  }

  function aggregateBanner(): HTMLElement {
    const value = aggregateScore(state.scores);
    const counted = Object.values(state.scores).filter((item) => typeof item === 'number').length;
    return el('div', { className: 'agg-banner' }, [
      el('span', { textContent: 'Aggregate' }),
      el('strong', { textContent: value == null ? '—' : value.toFixed(1) }),
      el('span', {
        className: 'muted',
        textContent: counted ? `${counted} scored` : 'No scores yet',
      }),
    ]);
  }

  function readBox(selector: string): string | null {
    const node = root.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement | null;
    return node ? node.value : null;
  }

  function readStep(): void {
    if (state.step === 1 && root.querySelector('[data-when]')) {
      state.observedAt = readBox('[data-when]') ?? state.observedAt;
      state.elements = who.getElements();
      state.tokens = who.getTokens();
      const here = place.get();
      state.lat = here.lat;
      state.lng = here.lng;
      state.accuracy = here.accuracy;
      state.description = here.description;
      const context = readBox('[data-context]');
      if (context != null) state.context = context;
    }
    if (state.step === 2) {
      readCriteria();
      const comment = readBox('[data-comment]');
      const cat = categories()[state.scoreIndex];
      if (comment != null && cat) {
        const text = comment.trim();
        if (text) state.comments[cat.id] = text;
        else delete state.comments[cat.id];
      }
    }
    if (state.step === 3 && root.querySelector('[data-strengths]')) {
      state.strengths = readBox('[data-strengths]') ?? state.strengths;
      state.deficiencies = readBox('[data-deficiencies]') ?? state.deficiencies;
      state.remediation = readBox('[data-remediation]') ?? state.remediation;
      state.narrative = readBox('[data-narrative]') ?? state.narrative;
      state.excon = readBox('[data-excon]') ?? state.excon;
      state.evidenceKeys = photos.keptKeys();
    }
  }

  function step1Problem(): string {
    if (!state.laneId) return 'Pick a lane';
    if (!state.observedAt) return 'Set the time';
    if (!state.elements.length) return 'Pick an element';
    if (!state.tokens.length) return 'Pick people or All for an element';
    const hasCoords = state.lat !== '' && state.lng !== '';
    if (!hasCoords && !state.description.trim()) {
      return 'Capture a location or describe the place';
    }
    if ((state.lat === '') !== (state.lng === '')) return 'Location needs both coordinates';
    return '';
  }

  function scoreProblem(): string {
    const cats = categories();
    if (!cats.length) return '';
    for (const cat of cats) {
      if (state.scores[cat.id] == null) return 'Score every category. Use N/A if you did not see it.';
    }
    return '';
  }

  function payload(keys: string[], status: ReportStatus): Record<string, unknown> {
    return {
      client_id: state.clientId,
      lane_id: state.laneId,
      observed_at: fromLocalInput(state.observedAt),
      element: state.elements[0] || '',
      elements: state.elements,
      targets: buildTargets(people, state.tokens),
      location_lat: state.lat === '' ? null : Number(state.lat),
      location_lng: state.lng === '' ? null : Number(state.lng),
      location_accuracy_m: state.accuracy === '' ? null : Number(state.accuracy),
      location_description: state.description.trim(),
      context: state.context.trim(),
      linked_spot_ids: state.linkedSpotIds,
      scores: state.scores,
      comments: state.comments,
      criteria: state.criteria.map((item) => ({
        id: item.id,
        title: item.title.trim() || 'Check',
        prompt: item.prompt.trim(),
        lookFor: item.lookFor.map((line) => line.trim()).filter(Boolean),
      })),
      strengths: state.strengths.trim(),
      deficiencies: state.deficiencies.trim(),
      remediation: state.remediation.trim(),
      narrative: state.narrative.trim(),
      excon_comments: state.excon.trim(),
      status,
      evidence_keys: keys,
      references: state.references,
    };
  }

  async function remember(): Promise<void> {
    await saveWorkingDraft(state, photos.pending());
  }

  async function sync(intent: 'draft' | 'pending'): Promise<'ok' | 'queued' | 'invalid'> {
    readStep();
    const sendStatus: ReportStatus =
      intent === 'pending' ? 'pending' : state.status === 'draft' ? 'draft' : state.status;
    const problem = step1Problem() || (sendStatus !== 'draft' ? scoreProblem() : '');
    await remember();
    if (problem) return 'invalid';
    const pending = photos.pending();
    const keys = [...photos.keptKeys()];
    const remaining: PendingPhoto[] = [];
    let uploadError: unknown = null;
    for (const photo of pending) {
      if (uploadError) {
        remaining.push(photo);
        continue;
      }
      try {
        keys.push(
          await uploadPhoto(
            new Blob([photo.data], { type: photo.contentType }),
            photo.contentType,
            photo.filename
          )
        );
      } catch (err) {
        uploadError = err;
        remaining.push(photo);
      }
    }
    const body = payload(keys, sendStatus);
    try {
      if (uploadError) throw uploadError;
      const saved = await saveLane(body);
      state.serverId = saved.id;
      state.evidenceKeys = saved.evidence_keys;
      state.status = saved.status;
      photos.setExisting(saved.evidence_keys);
      photos.setPending([]);
      await dropQueue(`lane:${state.clientId}`);
      await remember();
      return 'ok';
    } catch (err) {
      photos.setExisting(keys);
      photos.setPending(remaining);
      state.evidenceKeys = keys;
      await enqueue({
        id: `lane:${state.clientId}`,
        kind: 'lane_upsert',
        payload: body,
        photos: remaining,
        createdAt: new Date().toISOString(),
        status: 'pending',
        lastError: (err as Error).message,
      });
      await remember();
      const status = err instanceof ApiError ? err.status : 0;
      if (!isOffline(err) && status >= 400 && status < 500 && status !== 401) {
        toast((err as Error).message, 'err');
      }
      return 'queued';
    }
  }

  function showError(message: string): void {
    const node = root.querySelector('[data-error]');
    if (node) node.textContent = message;
  }

  async function goStep(next: 1 | 2 | 3): Promise<void> {
    readStep();
    if (state.step === 1 && next > 1) {
      const problem = step1Problem();
      if (problem) {
        showError(problem);
        return;
      }
    }
    if (state.step === 2 && next === 3) {
      const cat = categories()[state.scoreIndex];
      if (cat && state.scores[cat.id] == null) {
        showError('Pick a score or N/A');
        return;
      }
    }
    state.step = next;
    draw();
    snapTop();
    const result = await sync('draft');
    if (result === 'queued') {
      toast('Saved on this phone', 'ok');
    }
  }

  function snapTop(): void {
    window.scrollTo(0, 0);
  }

  function draw(): void {
    persist = true;
    root.replaceChildren();
    const title = el('div', { className: 'lane-top' }, [
      el('p', { className: 'kicker', textContent: `Step ${state.step} of 3` }),
      el('h1', {
        textContent: state.step === 1 ? 'Lane evaluation' : laneTitle(state.laneId) || 'Lane',
      }),
    ]);
    const fresh = el('button', {
      type: 'button',
      className: 'btn btn-ghost',
      textContent: 'Clear content/form',
    });
    fresh.addEventListener('click', () => {
      void (async () => {
        await dropQueue(`lane:${state.clientId}`);
        await clearWorkingDraft();
        state = blankState();
        photos.setExisting([]);
        photos.setPending([]);
        draw();
      })();
    });
    title.append(fresh);
    root.append(title);

    if (state.step === 1) drawStep1();
    else if (state.step === 2) drawStep2();
    else drawStep3();
  }

  function drawStep1(): void {
    const when = textInput({ type: 'datetime-local', value: state.observedAt });
    when.dataset.when = '1';
    who.set(state.elements, state.tokens);
    place.set({
      lat: state.lat,
      lng: state.lng,
      accuracy: state.accuracy,
      description: state.description,
    });
    const lanePick = el('select', { className: 'input' }) as HTMLSelectElement;
    lanePick.append(el('option', { value: '', textContent: 'Choose a lane' }));
    for (const lane of LANES) {
      const count = catalog[lane.id]?.length || 0;
      lanePick.append(
        el('option', {
          value: lane.id,
          textContent: count ? `${lane.title} · ${count} scores` : lane.title,
        })
      );
    }
    lanePick.value = state.laneId;
    lanePick.addEventListener('change', () => {
      readStep();
      if (state.laneId !== lanePick.value) {
        state.scores = {};
        state.comments = {};
        state.scoreIndex = 0;
        state.criteria = publishedCriteria(lanePick.value);
      }
      state.laneId = lanePick.value;
    });
    const context = textarea('Event or scenario notes');
    context.dataset.context = '1';
    context.value = state.context;
    const links = el('div', { className: 'stack' });
    const recent = spots.slice(0, 40);
    const linkPick = el('select', { className: 'input' }) as HTMLSelectElement;
    linkPick.append(el('option', { value: '', textContent: recent.length ? 'Add a spot report' : 'No spot reports yet' }));
    for (const spot of recent) {
      linkPick.append(
        el('option', {
          value: spot.id,
          textContent: `${fmtTime(spot.observed_at)} · ${spot.narrative.slice(0, 60) || 'Spot report'}`,
        })
      );
    }
    const chosen = el('div', { className: 'stack' });
    const paintLinks = () => {
      chosen.replaceChildren();
      for (const id of state.linkedSpotIds) {
        const spot = spots.find((item) => item.id === id);
        const row = el('div', { className: 'linked-row' });
        row.append(
          el('span', {
            textContent: spot
              ? `${fmtTime(spot.observed_at)} · ${spot.narrative.slice(0, 60) || 'Spot report'}`
              : 'Linked report',
          })
        );
        const remove = el('button', { type: 'button', className: 'btn btn-ghost', textContent: 'Remove' });
        remove.addEventListener('click', () => {
          state.linkedSpotIds = state.linkedSpotIds.filter((item) => item !== id);
          paintLinks();
        });
        row.append(remove);
        chosen.append(row);
      }
    };
    linkPick.addEventListener('change', () => {
      const id = linkPick.value;
      linkPick.value = '';
      if (!id || state.linkedSpotIds.includes(id)) return;
      state.linkedSpotIds = [...state.linkedSpotIds, id];
      paintLinks();
    });
    paintLinks();
    links.append(linkPick, chosen);
    const next = el('button', { type: 'button', className: 'btn btn-primary', textContent: 'Next' });
    next.addEventListener('click', () => void goStep(2));
    root.append(
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'Lane' }),
        lanePick,
      ]),
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'When' }),
        when,
      ]),
      who.element,
      place.element,
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'Context' }),
        context,
      ]),
      el('div', { className: 'block' }, [
        el('span', { className: 'field-label', textContent: 'Link spot reports' }),
        links,
      ]),
      el('p', { className: 'form-error', 'data-error': '1' }),
      el('div', { className: 'action-bar' }, [next])
    );
  }

  function drawStep2(): void {
    const cats = categories();
    if (!cats.length) {
      const next = el('button', { type: 'button', className: 'btn btn-primary', textContent: 'Next' });
      const back = el('button', { type: 'button', className: 'btn', textContent: 'Back' });
      next.addEventListener('click', () => void goStep(3));
      back.addEventListener('click', () => {
        state.step = 1;
        draw();
      });
      root.append(
        el('p', {
          className: 'lead',
          textContent:
            'No evaluation criteria for this lane yet. Publish them from Library, then Evaluation Criteria.',
        }),
        el('div', { className: 'action-bar' }, [back, next])
      );
      return;
    }
    const cat = cats[Math.min(state.scoreIndex, cats.length - 1)];
    state.scoreIndex = cats.indexOf(cat);
    const scores = el('div', { className: 'score-grid' });
    for (const n of [1, 2, 3, 4, 5] as const) {
      const on = state.scores[cat.id] === n;
      const btn = el('button', {
        type: 'button',
        className: on ? 'score on' : 'score',
        textContent: String(n),
        'aria-pressed': on ? 'true' : 'false',
      });
      btn.addEventListener('click', () => {
        readStep();
        state.scores[cat.id] = n;
        draw();
      });
      scores.append(btn);
    }
    const naOn = state.scores[cat.id] === 'na';
    const na = el('button', {
      type: 'button',
      className: naOn ? 'score on' : 'score',
      textContent: 'N/A',
      'aria-pressed': naOn ? 'true' : 'false',
    });
    na.addEventListener('click', () => {
      readStep();
      state.scores[cat.id] = 'na';
      draw();
    });
    scores.append(na);
    const comment = textarea('Optional');
    comment.dataset.comment = '1';
    comment.value = state.comments[cat.id] || '';
    const back = el('button', { type: 'button', className: 'btn', textContent: 'Back' });
    const next = el('button', {
      type: 'button',
      className: 'btn btn-primary',
      textContent: state.scoreIndex === cats.length - 1 ? 'Remarks' : 'Next',
    });
    back.addEventListener('click', () => {
      readStep();
      if (state.scoreIndex === 0) state.step = 1;
      else state.scoreIndex -= 1;
      draw();
    });
    next.addEventListener('click', () => {
      readStep();
      if (state.scores[cat.id] == null) {
        showError('Pick a score or N/A');
        return;
      }
      if (state.scoreIndex === cats.length - 1) void goStep(3);
      else {
        state.scoreIndex += 1;
        draw();
        snapTop();
        void remember();
      }
    });
    const bullets = el('ul', { className: 'epecl-list' });
    for (const line of cat.lookFor) bullets.append(el('li', { textContent: line }));
    root.append(
      aggregateBanner(),
      el('p', {
        className: 'kicker',
        textContent: `${state.scoreIndex + 1} of ${cats.length}`,
      }),
      el('h2', { textContent: `E-PECL ${state.scoreIndex + 1}: ${cat.title}` }),
      el('div', { className: 'block' }, [
        el('span', { className: 'field-label', textContent: 'Evaluation Criteria and Attributes' }),
        bullets,
      ]),
      el('p', { className: 'muted', textContent: SCORE_LEGEND }),
      scores,
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'Evaluator Comments' }),
        comment,
      ]),
      el('p', { className: 'form-error', 'data-error': '1' }),
      el('div', { className: 'action-bar' }, [back, next])
    );
  }

  function drawStep3(): void {
    const strengths = textarea('What held up');
    strengths.dataset.strengths = '1';
    strengths.value = state.strengths;
    const deficiencies = textarea('What has to change');
    deficiencies.dataset.deficiencies = '1';
    deficiencies.value = state.deficiencies;
    const remediation = textarea('What you recommend');
    remediation.dataset.remediation = '1';
    remediation.value = state.remediation;
    const narrative = textarea('What you saw on the lane');
    narrative.dataset.narrative = '1';
    narrative.value = state.narrative;
    const excon = textarea('For Internal AAR Use Only');
    excon.dataset.excon = '1';
    excon.value = state.excon;
    photos.setExisting(state.evidenceKeys);
    const back = el('button', { type: 'button', className: 'btn', textContent: 'Back' });
    const save = el('button', { type: 'button', className: 'btn', textContent: 'Save' });
    const submit = el('button', {
      type: 'button',
      className: 'btn btn-primary',
      textContent: 'Submit',
    });
    save.addEventListener('click', () => {
      void (async () => {
        save.disabled = true;
        const result = await sync('draft');
        if (!alive) return;
        toast(result === 'queued' ? 'Saved on this phone' : result === 'invalid' ? 'Add the lane, people, and place first' : 'Saved', result === 'invalid' ? 'err' : 'ok');
        save.disabled = false;
      })();
    });
    back.addEventListener('click', () => {
      readStep();
      const cats = categories();
      if (!cats.length) state.step = 1;
      else {
        state.step = 2;
        state.scoreIndex = cats.length - 1;
      }
      draw();
    });
    submit.addEventListener('click', () => {
      void (async () => {
        readStep();
        const problem = step1Problem() || scoreProblem();
        showError(problem);
        if (problem) {
          if (step1Problem()) state.step = 1;
          else {
            state.step = 2;
            const cats = categories();
            state.scoreIndex = Math.max(
              0,
              cats.findIndex((cat) => state.scores[cat.id] == null)
            );
          }
          draw();
          showError(problem);
          return;
        }
        submit.disabled = true;
        submit.textContent = 'Saving…';
        const result = await sync('pending');
        if (!alive) return;
        if (result === 'ok') {
          await clearWorkingDraft();
          toast('Submitted for review', 'ok');
          go('board');
          return;
        }
        if (result === 'queued') {
          toast('Saved on this phone. It will show on the board when it sends.', 'ok');
        }
        submit.disabled = false;
        submit.textContent = 'Submit';
      })();
    });
    root.append(
      aggregateBanner(),
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'Key strengths' }),
        strengths,
      ]),
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'Critical deficiencies' }),
        deficiencies,
      ]),
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'Remediation' }),
        remediation,
      ]),
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'Instructor narrative' }),
        narrative,
      ]),
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'EXCON comments' }),
        excon,
      ]),
      photos.element,
      el('p', { className: 'form-error', 'data-error': '1' }),
      el('div', { className: 'action-bar' }, [back, save, submit])
    );
  }

  root.replaceChildren(el('p', { className: 'muted', textContent: 'Loading…' }));

  void (async () => {
    try {
      catalog = await fetchLaneCriteria();
    } catch {
      catalog = {};
    }
    try {
      people = await fetchPeople();
      if (!alive) return;
      who.setPeople(people);
    } catch {
      /* names appear when the roster can be reached */
    }
    try {
      const feed = await fetchFeed();
      if (!alive) return;
      const cutoff = Date.now() - 36 * 60 * 60 * 1000;
      spots = feed.spots.filter((s) => new Date(s.observed_at).getTime() >= cutoff);
    } catch {
      spots = [];
    }
    const draft = await loadWorkingDraft();
    if (!alive) return;
    if (opts.fresh) {
      await clearWorkingDraft();
      state = blankState();
    } else if (opts.laneId) {
      try {
        const lane = await fetchLane(opts.laneId);
        if (!alive) return;
        const local = draft?.state as LaneState | undefined;
        const sameReport =
          !!local &&
          (local.clientId === lane.client_id || (!!local.serverId && local.serverId === lane.id));
        if (sameReport && local) {
          state = local;
          state.serverId = lane.id;
          state.clientId = lane.client_id || state.clientId;
          state.status = reportStatus(state.status);
          state.references = state.references || [];
          if (!state.criteria?.length) state.criteria = publishedCriteria(state.laneId);
          photos.setPending(draft?.photos || []);
        } else {
          state = fromServer(lane);
          photos.setPending([]);
        }
      } catch (err) {
        if (!alive) return;
        root.replaceChildren(
          el('p', {
            className: 'form-error',
            textContent: (err as Error).message || 'Could not open that lane report',
          })
        );
        return;
      }
    } else if (draft?.state) {
      state = draft.state as LaneState;
      state.status = reportStatus(state.status);
      state.references = state.references || [];
      if (!state.criteria?.length) state.criteria = publishedCriteria(state.laneId);
      photos.setPending(draft.photos || []);
    }
    if (!alive) return;
    photos.setExisting(state.evidenceKeys);
    draw();
  })();

  const onHide = () => {
    if (!persist) return;
    readStep();
    void remember();
  };
  window.addEventListener('pagehide', onHide);

  return () => {
    alive = false;
    if (persist) {
      readStep();
      void remember();
    }
    photos.dispose();
    window.removeEventListener('pagehide', onHide);
  };
}

export function laneDetail(
  root: HTMLElement,
  opts: { user: AuthUser; id: string }
): () => void {
  const host = el('div', { className: 'stack' });
  root.append(host);
  host.append(el('p', { className: 'muted', textContent: 'Loading…' }));
  let alive = true;
  async function paint(): Promise<void> {
    try {
      const lane = await fetchLane(opts.id);
      if (!alive) return;
      const bits: (Node | string)[] = [
        el('p', { className: 'kicker' }, [
          statusPill(lane.status),
          el('span', { textContent: fmtTime(lane.observed_at) }),
        ]),
        el('h1', { textContent: laneTitle(lane.lane_id) }),
        el('p', { className: 'lead', textContent: targetSummary(lane.targets) || 'No people tagged' }),
        el('p', {
          className: 'muted',
          textContent:
            formatPlace(lane.location_lat, lane.location_lng, lane.location_description) ||
            'No place',
        }),
        ...bylines(lane),
      ];
      if (lane.context) bits.push(labeled('Context', lane.context));
      const checklist = lane.criteria || [];
      if (checklist.length) {
        const list = el('div', { className: 'score-read' });
        for (const cat of checklist) {
          const value = lane.scores[cat.id];
          list.append(
            el('div', { className: 'score-line' }, [
              el('span', { textContent: `E-PECL ${checklist.indexOf(cat) + 1}: ${cat.title}` }),
              el('strong', {
                textContent: value == null ? '—' : value === 'na' ? 'N/A' : String(value),
              }),
            ])
          );
          if (cat.lookFor?.length) {
            const bullets = el('ul', { className: 'epecl-list' });
            for (const line of cat.lookFor) bullets.append(el('li', { textContent: line }));
            list.append(bullets);
          } else if (cat.prompt) {
            list.append(el('p', { textContent: cat.prompt }));
          }
          if (lane.comments[cat.id]) {
            list.append(el('p', { className: 'muted', textContent: lane.comments[cat.id] }));
          }
        }
        bits.push(list);
      } else {
        bits.push(el('p', { className: 'muted', textContent: 'No checklist on this lane.' }));
      }
      if (lane.strengths) bits.push(labeled('Key strengths', lane.strengths));
      if (lane.deficiencies) bits.push(labeled('Critical deficiencies', lane.deficiencies));
      if (lane.remediation) bits.push(labeled('Remediation', lane.remediation));
      if (lane.narrative) bits.push(labeled('Instructor narrative', lane.narrative));
      if (lane.excon_comments) {
        bits.push(labeled('EXCON comments (internal)', lane.excon_comments));
      }
      if (lane.references?.length) {
        bits.push(
          el('div', { className: 'block' }, [
            el('span', { className: 'field-label', textContent: 'References' }),
            referenceChips(lane.references),
          ])
        );
      }
      bits.push(photoGallery(lane.evidence_keys));
      bits.push(
        el('div', { className: 'agg-banner' }, [
          el('span', { textContent: 'Aggregate score' }),
          el('strong', { textContent: aggregateLabel(lane.scores) }),
        ])
      );
      host.replaceChildren(...bits);
      const exportBtn = el('button', { type: 'button', className: 'btn', textContent: 'Export PDF' });
      exportBtn.addEventListener('click', () => {
        void downloadReportPdf('lanes', lane.id).catch((err) => toast((err as Error).message, 'err'));
      });
      host.append(exportBtn);
      if (opts.user.role === 'admin') host.append(...reviewActions('lanes', lane.id, lane.status, paint));
      if (lane.author_id === opts.user.id || opts.user.role === 'admin') {
        const edit = el('button', { type: 'button', className: 'btn', textContent: 'Edit' });
        edit.addEventListener('click', () => go(`lane/${lane.id}`));
        let armed = false;
        const remove = el('button', { type: 'button', className: 'btn btn-ghost', textContent: 'Delete' });
        remove.addEventListener('click', () => {
          if (!armed) {
            armed = true;
            remove.textContent = 'Tap again to delete';
            return;
          }
          void deleteLane(lane.id)
            .then(() => go('board'))
            .catch((err) => toast((err as Error).message, 'err'));
        });
        host.append(edit, remove);
      }
    } catch (err) {
      if (alive) {
        host.replaceChildren(
          el('p', { className: 'form-error', textContent: (err as Error).message })
        );
      }
    }
  }
  void paint();
  return () => {
    alive = false;
  };
}

function reviewActions(
  kind: 'spots' | 'lanes',
  id: string,
  status: ReportStatus,
  onDone: () => Promise<void>
): HTMLButtonElement[] {
  const specs: { label: string; review: 'returned' | 'approved'; action: string }[] = [
    { label: 'Return for Corrections', review: 'returned', action: 'return' },
    { label: 'Approved', review: 'approved', action: 'approve' },
  ];
  return specs.map(({ label, review, action }) => {
    const btn = el('button', {
      type: 'button',
      className: 'btn',
      textContent: label,
      'data-action': action,
    });
    if (status === review) btn.disabled = true;
    btn.addEventListener('click', () => {
      btn.disabled = true;
      void reviewReport(kind, id, review)
        .then(() => {
          toast(review === 'approved' ? 'Approved' : 'Returned for corrections', 'ok');
          return onDone();
        })
        .catch((err) => {
          btn.disabled = false;
          toast((err as Error).message, 'err');
        });
    });
    return btn;
  });
}

function labeled(title: string, text: string): HTMLElement {
  return el('div', { className: 'block' }, [
    el('span', { className: 'field-label', textContent: title }),
    el('p', { textContent: text }),
  ]);
}
